import type { ElementType } from "react";
import { Link } from "wouter";
import { Facebook, Instagram, MapPin, Mail, Phone, Twitter, Youtube, Linkedin, Globe } from "lucide-react";
import { useCmsSections } from "@/hooks/use-cms";
import { useQuery } from "@tanstack/react-query";
import { db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";

function TikTokIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V9.02a8.27 8.27 0 0 0 4.84 1.55V7.12a4.85 4.85 0 0 1-1.07-.43z" />
    </svg>
  );
}

function parseMeta(raw: string | null | undefined): Record<string, any> {
  if (!raw) return {};
  try { return JSON.parse(raw); } catch { return {}; }
}

function useSiteSettings() {
  return useQuery<Record<string, string>>({
    queryKey: ["configs", "generalSettings"],
    queryFn: async () => {
      try {
        const snap = await getDoc(doc(db, "configs", "generalSettings"));
        return snap.exists() ? (snap.data() as Record<string, string>) : {};
      } catch {
        return {};
      }
    },
    staleTime: 1000 * 60 * 10,
  });
}

export function Footer() {
  const currentYear = new Date().getFullYear();
  const { data: sections = [] } = useCmsSections();
  const { data: siteSettings = {} } = useSiteSettings();

  const footerSection = Array.isArray(sections) ? sections.find((s: any) => s.key === "footer") : undefined;
  const footerSocialSection = Array.isArray(sections) ? sections.find((s: any) => s.key === "footer_social") : undefined;

  const m = parseMeta(footerSection?.metadata);

  const tagline = footerSection?.content || "An international sports management organization committed to discovering and nurturing exceptional athletes worldwide.";
  const address = m.address || "Unterseilberg 9, 94143,\nGrainet, Bayern, Germany";
  const phone = siteSettings.contact_phone || m.phone || "+49 15770 904476";
  const email = siteSettings.contact_email || m.email || "contact@eliadasportsagency.com";
  const copyrightText = m.copyrightText || "Eliada Sports Agency. All rights reserved.";
  const privacyUrl = m.privacyUrl || "/privacy-policy";
  const termsUrl = m.termsUrl || "/terms-of-service";

  const DEFAULT_QUICK_LINKS = [
    { label: "About Us", href: "/about" },
    { label: "Our Services", href: "/services" },
    { label: "Athletes", href: "/athletes" },
    { label: "Our Team", href: "/team" },
    { label: "Gallery", href: "/gallery" },
    { label: "Partner With Us", href: "/partner" },
    { label: "Contact Us", href: "/contact" },
  ];

  const quickLinks: { label: string; href: string }[] = (() => {
    if (siteSettings.footer_quick_links) {
      try { return JSON.parse(siteSettings.footer_quick_links); } catch { /* fall through */ }
    }
    return m.quickLinks || DEFAULT_QUICK_LINKS;
  })();

  const expertiseLinks: { label: string; href: string }[] = m.expertiseLinks || [
    { label: "Football Academy", href: "/services/football-academy" },
    { label: "Talent Scouting", href: "/services/talent-scouting" },
    { label: "Player Representation", href: "/services/player-representation" },
    { label: "International Transfers", href: "/services/international-transfers" },
    { label: "Career Investment", href: "/services/career-investment" },
  ];

  const FOOTER_ICON_MAP: Record<string, ElementType> = {
    Facebook, Instagram, Twitter, Youtube, Linkedin, TikTok: TikTokIcon, Globe,
  };


  const cmsItems: Array<{ name: string; icon: string; url: string }> = parseMeta(footerSocialSection?.metadata).items ?? [];

  const socialLinks = cmsItems.filter(it => it.url).map(it => ({
    href: it.url,
    icon: FOOTER_ICON_MAP[it.icon] ?? Globe,
    label: it.name,
  }));

  return (
    <footer className="bg-[#05080f] border-t border-white/5 pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Main grid — 4 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

          {/* ── Brand ── */}
          <div className="space-y-6 lg:col-span-1">
            <Link href="/" className="flex items-center gap-3 group">
              <img
                src={`${import.meta.env.BASE_URL}images/logo.png`}
                alt="Eliada Sports Agency Logo"
                className="w-11 h-11 object-contain group-hover:scale-105 transition-transform"
              />
              <div className="flex flex-col">
                <span className="font-display font-bold text-xl tracking-widest text-white leading-none">ELIADA</span>
                <span className="font-display font-medium text-xs tracking-[0.22em] text-primary leading-none mt-1">SPORTS AGENCY</span>
              </div>
            </Link>

            <p className="text-gray-500 text-sm leading-relaxed">{tagline}</p>

            {/* Social icons */}
            {socialLinks.length > 0 && (
              <div className="flex gap-3 flex-wrap">
                {socialLinks.map(({ href, icon: Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="w-9 h-9 rounded-lg bg-white/5 border border-white/8 flex items-center justify-center text-gray-500 hover:text-primary hover:bg-primary/10 hover:border-primary/30 transition-all"
                  >
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* ── Quick Links ── */}
          <div>
            <h4 className="font-display text-xs font-bold tracking-[0.2em] uppercase text-primary mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map(link => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-gray-500 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-primary/40 group-hover:bg-primary transition-colors shrink-0" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Expertise ── */}
          <div>
            <h4 className="font-display text-xs font-bold tracking-[0.2em] uppercase text-primary mb-6">Expertise</h4>
            <ul className="space-y-3">
              {expertiseLinks.map(link => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-gray-500 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-primary/40 group-hover:bg-primary transition-colors shrink-0" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Contact ── */}
          <div>
            <h4 className="font-display text-xs font-bold tracking-[0.2em] uppercase text-primary mb-6">Contact</h4>
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/8 flex items-center justify-center shrink-0 mt-0.5">
                  <MapPin size={14} className="text-primary" />
                </div>
                <span className="text-gray-500 text-sm leading-relaxed whitespace-pre-line">{address}</span>
              </li>
              {phone && (
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/8 flex items-center justify-center shrink-0">
                    <Phone size={14} className="text-primary" />
                  </div>
                  <span className="text-gray-500 text-sm">{phone}</span>
                </li>
              )}
              {email && (
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/8 flex items-center justify-center shrink-0">
                    <Mail size={14} className="text-primary" />
                  </div>
                  <a href={`mailto:${email}`} className="text-gray-500 hover:text-primary transition-colors text-sm whitespace-nowrap">
                    {email}
                  </a>
                </li>
              )}
            </ul>
          </div>

        </div>

        {/* ── Divider ── */}
        <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-600 text-xs">
            © {currentYear} {copyrightText}
          </p>
          <div className="flex items-center gap-5 text-xs text-gray-600">
            {privacyUrl && (
              <Link href={privacyUrl} className="hover:text-gray-400 transition-colors">
                Privacy Policy
              </Link>
            )}
            {privacyUrl && termsUrl && <span className="text-gray-700">·</span>}
            {termsUrl && (
              <Link href={termsUrl} className="hover:text-gray-400 transition-colors">
                Terms of Service
              </Link>
            )}
          </div>
        </div>

      </div>
    </footer>
  );
}
